import { CalendarDays, MessageSquare, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { Section } from '@/components/common/Blocks'
import { EmptyState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useMeetings } from '../../api'
import { CHANNELS, RESULTS, type Channel, type Meeting, type MeetingResult } from '../../types'
import { useCrm } from '../../ui'
import type { CrmDialog } from '../CrmDialogs'
import { MeetingsTable } from '../MeetingsTable'
import { useCrmDeletes } from '../useCrmDeletes'
import { MeetingDetail } from './MeetingDetail'

const RANGES = ['all', '7', '30', '90'] as const
/** Seçilen aralığın başlangıç günü (yyyy-MM-dd). */
const sinceOf = (range: (typeof RANGES)[number]) => (range === 'all' ? '' : new Date(Date.now() - Number(range) * 86_400_000).toISOString().slice(0, 10))

export function MeetingsTab({ customerId, canWrite, open }: { customerId: string; canWrite: boolean; open: (d: CrmDialog) => void }) {
  const { t } = useTranslation()
  const meetings = useMeetings(customerId)
  const deletes = useCrmDeletes()
  const { staff } = useCrm()
  const { can, user } = useAuth()
  // Başkasının toplantı tutanağı değiştirilemez: sahip, katılımcı veya crm:note:manage.
  const canEdit = (m: Meeting) => canWrite && (can('crm:note:manage') || m.ownerId === user?.id || m.staffIds.includes(user?.id ?? ''))
  const [params, setParams] = useSearchParams()
  const [staffId, setStaffId] = useState('all')
  const [channel, setChannel] = useState<Channel | 'all'>('all')
  const [result, setResult] = useState<MeetingResult | 'all'>('all')
  const [range, setRange] = useState<(typeof RANGES)[number]>('all')

  const all = meetings.data ?? []
  const since = sinceOf(range)
  const rows = all.filter((m) => (staffId === 'all' || m.staffId === staffId) && (channel === 'all' || m.channel === channel) && (result === 'all' || m.result === result) && (!since || m.date >= since))
  const selectedId = params.get('m')
  const selected = all.find((m) => m.id === selectedId) ?? null
  const select = (id: string | null) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p)
        if (id) n.set('m', id)
        else n.delete('m')
        return n
      },
      { replace: true },
    )
  const filter = <V extends string>(id: string, label: string, value: V, onChange: (v: V) => void, options: { value: V; label: string }[]) => (
    <div className="min-w-0 space-y-1">
      <label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </label>
      <Select id={id} size="sm" value={value} onChange={onChange} options={options} />
    </div>
  )
  const onDelete = async (m: Meeting) => {
    if (await deletes.meeting(m)) select(null)
  }

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_26rem] 2xl:grid-cols-[minmax(0,1fr)_30rem]">
      <Section
        title={t('crm.meeting.listTitle')}
        icon={MessageSquare}
        bodyClassName="p-0"
        actions={
          canWrite ? (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => open({ kind: 'meeting', meeting: null, plan: true })}>
                <CalendarDays />
                {t('crm.meeting.plan')}
              </Button>
              <Button size="sm" onClick={() => open({ kind: 'meeting', meeting: null })}>
                <Plus />
                {t('crm.meeting.new')}
              </Button>
            </div>
          ) : null
        }
      >
        <div className="grid grid-cols-2 gap-2 border-b p-3 lg:grid-cols-4">
          {filter('mf-staff', t('crm.fields.staff'), staffId, setStaffId, [{ value: 'all', label: t('common.all') }, ...staff.map((s) => ({ value: s.id, label: s.name }))])}
          {filter('mf-channel', t('crm.fields.channel'), channel, setChannel, [{ value: 'all', label: t('common.all') }, ...CHANNELS.map((c) => ({ value: c, label: t(`crm.channel.${c}`) }))])}
          {filter('mf-result', t('crm.fields.result'), result, setResult, [{ value: 'all', label: t('common.all') }, ...RESULTS.map((r) => ({ value: r, label: t(`crm.result.${r}`) }))])}
          {filter('mf-range', t('crm.fields.date'), range, setRange, RANGES.map((r) => ({ value: r, label: t(`crm.range.${r}`) })))}
        </div>
        {rows.length === 0 ? <EmptyState icon={MessageSquare} title={t('crm.meeting.empty')} /> : <MeetingsTable rows={rows} selectedId={selected?.id} onSelect={(m) => select(m.id)} />}
      </Section>
      {selected ? (
        <MeetingDetail
          key={selected.id}
          meeting={selected}
          canWrite={canEdit(selected)}
          onClose={() => select(null)}
          onEdit={() => open({ kind: 'meeting', meeting: selected })}
          onDelete={() => void onDelete(selected)}
          onNew={(plan) => open({ kind: 'meeting', meeting: null, plan })}
        />
      ) : (
        <div className="hidden rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground xl:block">{t('crm.meeting.selectHint')}</div>
      )}
    </div>
  )
}
